import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getFilters } from 'nallian-shared-ui/lib/hoc/redux-filters';
import { Table, Params, Actions, Menu } from 'nallian-table-builder-ui';
import { Flex, Button } from 'nallian-shared-ui/lib/components';
import { useTranslation } from 'react-i18next';
import { showToaster } from 'nallian-shared-ui/lib/lib/showToaster';
import { GetRights, GetMyPrincipal } from 'nallian-shared-ui/lib/selectors';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { DocumentStatus, DocumentType } from 'nallian-shared-ui/lib/layout/documents';
import apiRoutes from '../../../../../app/api/routes';
import {
  DOCUMENT_ACTIONS,
  DOCUMENT_FILTER_SETTINGS,
  DOCUMENT_MODAL_TYPES,
  DOCUMENT_SORT_SETTINGS,
  INFINITE_SCROLL,
  PRINCIPAL_TABS,
} from '../../../constants';
import principalDocumentsService from '../../../services/documents/principalDocumentsService';
import DocumentHistoryModal from './DocumentHistoryModal';
import DocumentNameRenderer from './DocumentNameRenderer';
import DocumentPreviewModal from './DocumentPreviewModal';
import DocumentUploadModal from './DocumentUploadModal';

const base64ToBlob = (base64, mimeType = 'image/jpeg') => {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeType });
};

const PrincipalDocumentsTab = ({
  principalId,
  canManage = false,
  urlRoot,
  action,
  dispatchRefreshPrincipalDocumentsGet,
}) => {
  const { t } = useTranslation(['app', 'shared']);
  const navigate = useNavigate();
  const location = useLocation();
  const [modal, setModal] = useState({ type: null, item: null });
  const closeModal = useCallback(() => setModal({ type: null, item: null }), []);

  const RESOURCE = `principalDocuments_${principalId}`;
  const currentFilters = useSelector(state => getFilters(state, `${RESOURCE}OverviewFilters`));
  const rights = useSelector(GetRights);
  const myPrincipal = useSelector(GetMyPrincipal);
  const canPreview =
    hasRight(rights, 'principals/documents/preview') || principalId === myPrincipal?.id;

  const handlePreviewClick = useCallback(
    (_, item) => setModal({ type: DOCUMENT_MODAL_TYPES.PREVIEW, item }),
    []
  );

  const handleConfirmUpload = useCallback(
    async (docItem, redactionData) => {
      try {
        const formData = new FormData();
        formData.append('documentType', docItem.documentType);
        formData.append('expiresAt', new Date(docItem.expiresAt).toISOString());

        const blob = base64ToBlob(redactionData.redactedImage);
        formData.append('file', blob, docItem.fileName || 'document.jpg');

        await axios.post(apiRoutes.confirmDocument(principalId), formData, {
          headers: { 'content-type': undefined },
        });

        showToaster('success', t('documents.documentConfirmed'));
        navigate(`${urlRoot}/${PRINCIPAL_TABS.DOCUMENTS}`);
        dispatchRefreshPrincipalDocumentsGet(principalId, currentFilters);
      } catch (err) {
        const data = err?.response?.data;
        const errorMessage = Array.isArray(data)
          ? data.map(c => (c.errors || []).join(' ')).join(' ')
          : t('documents.confirmFailed');
        showToaster('error', errorMessage || t('documents.confirmFailed'));
      }
    },
    [principalId, urlRoot, currentFilters, navigate, dispatchRefreshPrincipalDocumentsGet, t]
  );

  const handleViewHistory = useCallback(
    item => setModal({ type: DOCUMENT_MODAL_TYPES.HISTORY, item }),
    []
  );

  const documentNameRenderer = useCallback(
    props => <DocumentNameRenderer {...props} onViewHistory={handleViewHistory} />,
    [handleViewHistory]
  );

  const handleUploadClick = useCallback(
    () => navigate(`${urlRoot}/${PRINCIPAL_TABS.DOCUMENTS}/${DOCUMENT_ACTIONS.UPLOAD}`),
    [navigate, urlRoot]
  );

  const handleUploadComplete = useCallback(
    ({ documentType, expiresAt, redactionZones, imageDataUrl, fileName }) => {
      navigate(`${urlRoot}/${PRINCIPAL_TABS.DOCUMENTS}/${DOCUMENT_ACTIONS.PREVIEW}`, {
        state: { documentType, expiresAt, redactionZones, imageDataUrl, fileName },
      });
    },
    [navigate, urlRoot]
  );

  return (
    <>
      <Flex justifyContent="flex-start" alignItems="center">
        {canManage && (
          <Button
            btnprimary
            icon="upload"
            content={t('documents.upload')}
            onClick={handleUploadClick}
          />
        )}
      </Flex>
      <Table
        infiniteScroll={INFINITE_SCROLL}
        resource={RESOURCE}
        url={apiRoutes.principalDocuments(principalId)}
        filtered={DOCUMENT_FILTER_SETTINGS}
        noResultsMessage={t('documents.noDocumentsFound')}
        pageSize={50}
        sorted={DOCUMENT_SORT_SETTINGS}
        noUserSettings
      >
        {canPreview && (
          <Actions.Button
            iconbutton
            width="2.5rem"
            align="center"
            icon="eye"
            onClick={handlePreviewClick}
          />
        )}
        <Params.Custom
          name="fileName"
          label="documentName"
          content={documentNameRenderer}
          width="20rem"
          minWidth="20rem"
          noFilter
          noSort
          grow
        />
        <Params.Custom
          name="documentType"
          label="documentType"
          content={DocumentType}
          width="12.5rem"
          minWidth="12.5rem"
          noFilter
          noSort
          grow
        />
        <Params.String
          name="uploadedBy.fullName"
          label="uploadedBy"
          width="10rem"
          minWidth="10rem"
          noSort
          noFilter
        />
        <Params.DateTime name="createdOn" label="uploadedAt" width="10rem" noFilter noSort />
        <Params.Custom
          name="status"
          label="status"
          content={DocumentStatus}
          width="8rem"
          minWidth="8rem"
          noFilter
          noSort
        />
        <Params.DateTime name="expiresAt" label="expirationDate" width="10rem" noFilter noSort />
        {canPreview && (
          <Menu width="3.5rem" align="center">
            <Actions.Button
              icon="eye"
              label={t('documents.preview')}
              content={t('documents.preview')}
              onClick={handlePreviewClick}
            />
          </Menu>
        )}
      </Table>
      {modal.type === DOCUMENT_MODAL_TYPES.HISTORY && (
        <DocumentHistoryModal
          item={modal.item}
          principalId={principalId}
          canPreview={canPreview}
          onClose={closeModal}
        />
      )}
      {modal.type === DOCUMENT_MODAL_TYPES.PREVIEW && (
        <DocumentPreviewModal item={modal.item} principalId={principalId} onClose={closeModal} />
      )}
      {action === DOCUMENT_ACTIONS.PREVIEW && location.state?.imageDataUrl && !modal.type && (
        <DocumentPreviewModal
          item={{
            documentType: location.state.documentType,
            expiresAt: location.state.expiresAt,
            fileName: location.state.fileName,
          }}
          principalId={principalId}
          isRedacting
          imageDataUrl={location.state.imageDataUrl}
          initialRedactionZones={location.state.redactionZones}
          onClose={() => navigate(`${urlRoot}/${PRINCIPAL_TABS.DOCUMENTS}`)}
          onApprove={handleConfirmUpload}
        />
      )}
      {action === DOCUMENT_ACTIONS.UPLOAD && canManage && (
        <DocumentUploadModal
          principalId={principalId}
          onClose={() => navigate(`${urlRoot}/${PRINCIPAL_TABS.DOCUMENTS}`)}
          onUploadComplete={handleUploadComplete}
        />
      )}
    </>
  );
};

PrincipalDocumentsTab.propTypes = {
  principalId: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
  urlRoot: PropTypes.string.isRequired,
  action: PropTypes.string,
  dispatchRefreshPrincipalDocumentsGet: PropTypes.func.isRequired,
};

export default principalDocumentsService(PrincipalDocumentsTab);
